import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import styles from "./clear-braces.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  subtitle: string;
  introduction: string;
  // Blend In With Clear Braces Section
  blendInTitle: string;
  blendInContent1: string;
  blendInContent2: string;
  blendInContent3: string;
  blendInContent4: string;
  // Differences Between Ceramic & Metal Braces Section
  differencesTitle: string;
  differencesContent1: string;
  differencesContent2: string;
  differencesContent3: string;
  differencesContent4: string;
  differencesContent5: string;
  // Cost of Ceramic Braces Section
  costTitle: string;
  costContent1: string;
  costContent2: string;
  costContent3: string;
  costContent4: string;
  // A Clear Alternative Section
  alternativeTitle: string;
  alternativeContent1: string;
  alternativeContent2: string;
  alternativeContent3: string;
  alternativeContent4: string;
};

const englishLocalization: Localization = {
  title: "Clear Braces",
  subtitle: "A Clear Alternative to Metal Braces",
  introduction:
    "Ceramic braces are sometimes called clear braces, tooth-colored braces, or aesthetic braces. They work the same way metal braces do.",

  // Blend In With Clear Braces Section
  blendInTitle: "Blend In With Clear Braces",
  blendInContent1:
    "Ceramic brackets are a less obvious way to straighten your smile in Miami when paired with clear, white, or tooth-colored wires and elastic ties. This option is especially attractive if you are an esthetic-minded adult or teen.",
  blendInContent2:
    "Ceramic braces are perfect if you're talking to Dr. Garcia-Paul and you want a subtle treatment option but don't want the extra hassle of removable aligners and the responsibility of wear time with clear aligner systems like Invisalign®.",
  blendInContent3:
    "Even though most Miami patients in ceramic braces love how discrete they are, Dr. Garcia-Paul does have some teens and adults who like to use colored ligature ties. This gives braces an almost neon look.",
  blendInContent4:
    "The colors pop off the clear brackets, creating a really unique look most people in Miami haven't seen before.",

  // Differences Between Ceramic & Metal Braces Section
  differencesTitle: "Differences Between Ceramic & Metal Braces",
  differencesContent1:
    "Metal braces are known for their durability and strength. Ceramic braces are also tough, but they are made from a different, more fragile material that is more likely to chip or crack. Traditional braces are also better at tolerating pressure than newer ceramic ones.",
  differencesContent2:
    "If you need more drastic corrections, metal braces could be a better option. Adjustments with ceramic braces have to be slightly smaller, so it can take a little more time to align your teeth.",
  differencesContent3:
    "Having said that, Dr. Garcia-Paul uses both types of braces to work on the same principles and tend to be equally as effective in most cases.",
  differencesContent4:
    "You may be worried about ceramic braces staining, but that hasn't been a problem for several years. Ceramic brackets won't stain. But, the adhesive used to attach the brackets to the teeth and the clear elastic ties used to hold the wires in can stain.",
  differencesContent5:
    "This can give an overall impression of discoloration or staining over time. You can choose to avoid things like coffee and tea, red wine, tomatoes, mustard, etc. to cut back on the chance of any staining, but you don't need to. We change your elastics at every adjustment appointment. As long as you maintain a good oral hygiene routine, you shouldn't have to worry too much about giving up your morning cup of coffee or evening glass of wine.",

  // Cost of Ceramic Braces Section
  costTitle: "Cost of Ceramic Braces",
  costContent1:
    "One of the biggest factors that affect the cost of your treatment will be the materials used to make your braces.",
  costContent2:
    "In our Miami office, ceramic brackets cost a little more than metal brackets.",
  costContent3:
    "The good news is, we offer flexible payment plans, automatic bank drafts, and we will also check with your insurance company for orthodontic benefits. Our goal is to provide outstanding value for reasonable fees!",
  costContent4:
    "Treatment fees will vary depending on the complexity of your orthodontic treatment. Dr. Garcia-Paul will determine a treatment fee that fits your specific orthodontic needs, and our office will do everything we can to accommodate you and your budget. Our treatment coordinators will be happy to go over the cost with you at your first free consultation.",

  // A Clear Alternative Section
  alternativeTitle: "A Clear Alternative to Metal Braces",
  alternativeContent1:
    "Ceramic braces are sometimes called clear braces, tooth-colored braces, or aesthetic braces. They work the same way metal braces do.",
  alternativeContent2:
    "Our clear braces at Miami Ortho Specialists are designed to be less noticeable on your teeth than metal braces. They're smaller than ever and made of nearly invisible, translucent polycrystalline ceramic material.",
  alternativeContent3:
    "Dr. Garcia-Paul will use these ceramic options will blend in well with your natural smile.",
  alternativeContent4:
    "Though they're not completely invisible, and the brackets are about the same size and shape, they will be harder to notice, compared to metal braces.",
};

const spanishLocalization: Localization = {
  title: "Brackets transparentes",
  subtitle: "Una alternativa transparente a los brackets metálicos",
  introduction:
    "Los brackets cerámicos a veces se llaman brackets transparentes, brackets del color de los dientes o brackets estéticos. Funcionan de la misma manera que los brackets metálicos.",

  // Blend In With Clear Braces Section
  blendInTitle: "Disimula tu tratamiento con brackets transparentes",
  blendInContent1:
    "Los brackets cerámicos son una forma menos obvia de enderezar tu sonrisa en Miami cuando se combinan con alambres y ligaduras elásticas transparentes, blancas o del color de los dientes. Esta opción es especialmente atractiva si eres un adulto o adolescente consciente de la estética.",
  blendInContent2:
    "Los brackets cerámicos son perfectos si estás hablando con la Dra. García-Paul y quieres una opción de tratamiento sutil, pero no quieres la molestia adicional de los alineadores removibles ni la responsabilidad de usarlos durante el tiempo indicado con sistemas de alineadores transparentes como Invisalign®.",
  blendInContent3:
    "Aunque a la mayoría de los pacientes de Miami con brackets cerámicos les encanta lo discretos que son, la Dra. García-Paul tiene algunos adolescentes y adultos a quienes les gusta usar ligaduras de colores. Esto le da a los brackets un aspecto casi neón.",
  blendInContent4:
    "Los colores resaltan sobre los brackets transparentes, creando un aspecto realmente único que la mayoría de las personas en Miami no han visto antes.",

  // Differences Between Ceramic & Metal Braces Section
  differencesTitle: "Diferencias entre brackets cerámicos y metálicos",
  differencesContent1:
    "Los brackets metálicos son conocidos por su durabilidad y resistencia. Los brackets cerámicos también son resistentes, pero están hechos de un material diferente, más frágil, que tiene más probabilidades de astillarse o agrietarse. Los brackets tradicionales también son mejores para tolerar la presión que los cerámicos más nuevos.",
  differencesContent2:
    "Si necesitas correcciones más drásticas, los brackets metálicos podrían ser una mejor opción. Los ajustes con brackets cerámicos tienen que ser ligeramente más pequeños, por lo que puede tomar un poco más de tiempo alinear tus dientes.",
  differencesContent3:
    "Dicho esto, la Dra. García-Paul utiliza ambos tipos de brackets para trabajar con los mismos principios y tienden a ser igualmente efectivos en la mayoría de los casos.",
  differencesContent4:
    "Puede que te preocupe que los brackets cerámicos se manchen, pero eso no ha sido un problema durante varios años. Los brackets cerámicos no se manchan. Pero, el adhesivo utilizado para unir los brackets a los dientes y las ligaduras elásticas transparentes utilizadas para sujetar los alambres pueden mancharse.",
  differencesContent5:
    "Esto puede dar una impresión general de decoloración o manchas con el tiempo. Puedes optar por evitar cosas como café y té, vino tinto, tomates, mostaza, etc. para reducir la posibilidad de manchas, pero no es necesario. Cambiamos tus elásticos en cada cita de ajuste. Mientras mantengas una buena rutina de higiene bucal, no deberías preocuparte demasiado por renunciar a tu taza de café matutina o copa de vino nocturna.",

  // Cost of Ceramic Braces Section
  costTitle: "Costo de los brackets cerámicos",
  costContent1:
    "Uno de los factores más importantes que afectan el costo de tu tratamiento serán los materiales utilizados para hacer tus brackets.",
  costContent2:
    "En nuestra oficina de Miami, los brackets cerámicos cuestan un poco más que los brackets metálicos.",
  costContent3:
    "La buena noticia es que ofrecemos planes de pago flexibles, débitos bancarios automáticos y también verificaremos con tu compañía de seguros los beneficios ortodónticos. ¡Nuestro objetivo es ofrecer un excelente valor por honorarios razonables!",
  costContent4:
    "Los honorarios del tratamiento variarán dependiendo de la complejidad de tu tratamiento ortodóntico. La Dra. García-Paul determinará los honorarios del tratamiento que se ajusten a tus necesidades ortodónticas específicas, y nuestra oficina hará todo lo posible para adaptarnos a ti y a tu presupuesto. Nuestros coordinadores de tratamiento estarán encantados de revisar el costo contigo en tu primera consulta gratuita.",

  // A Clear Alternative Section
  alternativeTitle: "Una alternativa transparente a los brackets metálicos",
  alternativeContent1:
    "Los brackets cerámicos a veces se llaman brackets transparentes, brackets del color de los dientes o brackets estéticos. Funcionan de la misma manera que los brackets metálicos.",
  alternativeContent2:
    "Nuestros brackets transparentes en Miami Ortho Specialists están diseñados para ser menos visibles en tus dientes que los brackets metálicos. Son más pequeños que nunca y están hechos de material cerámico policristalino translúcido, casi invisible.",
  alternativeContent3:
    "La Dra. García-Paul usará estas opciones cerámicas para que se integren bien con tu sonrisa natural.",
  alternativeContent4:
    "Aunque no son completamente invisibles, y los brackets son aproximadamente del mismo tamaño y forma, serán más difíciles de notar, en comparación con los brackets metálicos.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function ClearBracesPage() {
  const getLocalization = useLocalization(localized);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/clear-braces-hero.jpg"
        title={getLocalization("title")}
        backgroundPosition="top"
      />

      {/* A Clear Alternative Section with gray background */}
      <Box className={styles.alternativeSection}>
        <Box className={styles.alternativeContainer}>
          <Flex direction="column" gap="6">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("alternativeTitle")}
            </Heading>
            <Grid columns="12" gap="6">
              <Flex
                direction="column"
                gap="5"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Text size="5">{getLocalization("alternativeContent1")}</Text>
                <Text size="5">{getLocalization("alternativeContent2")}</Text>
              </Flex>
              <Flex
                direction="column"
                gap="5"
                gridColumn={{ initial: "1 / 13", sm: "7 / 13" }}
              >
                <Text size="5">{getLocalization("alternativeContent3")}</Text>
                <Text size="5">{getLocalization("alternativeContent4")}</Text>
              </Flex>
            </Grid>
          </Flex>
        </Box>
      </Box>

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="5"
        >
          {/* Blend In With Clear Braces Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("blendInTitle")}
            </Heading>
            <Grid columns="12" gap="6">
              <Flex
                direction="column"
                gap="4"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Text size="5">{getLocalization("blendInContent1")}</Text>
                <Text size="5">{getLocalization("blendInContent2")}</Text>
              </Flex>
              <Flex
                direction="column"
                gap="4"
                gridColumn={{ initial: "1 / 13", sm: "7 / 13" }}
              >
                <Text size="5">{getLocalization("blendInContent3")}</Text>
                <Text size="5">{getLocalization("blendInContent4")}</Text>
              </Flex>
            </Grid>

            {/* Image after Blend In section */}
            <Box mt="6" width="100%" style={{ textAlign: "center" }}>
              <OptimizedImage
                src="/clear-braces.jpg"
                alt="Miami Orthodontics Clear Braces"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Flex>

          {/* Differences Between Ceramic & Metal Braces Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("differencesTitle")}
            </Heading>
            <Flex direction="column" gap="4">
              <Text size="5">{getLocalization("differencesContent1")}</Text>
              <Text size="5">{getLocalization("differencesContent2")}</Text>
              <Text size="5">{getLocalization("differencesContent3")}</Text>
              <Text size="5">{getLocalization("differencesContent4")}</Text>
              <Text size="5">{getLocalization("differencesContent5")}</Text>
            </Flex>
          </Flex>

          {/* Cost of Ceramic Braces Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("costTitle")}
            </Heading>
            <Flex direction="column" gap="4">
              <Text size="5">{getLocalization("costContent1")}</Text>
              <Text size="5">{getLocalization("costContent2")}</Text>
              <Text size="5">{getLocalization("costContent3")}</Text>
              <Text size="5">{getLocalization("costContent4")}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
